import { useLearningUiTranslation } from '@features/learning/shared/use-learning-ui-translation'
import { createCachedLearningAudio } from '@shared/media/audio/learning-audio-cache'
import { useWarmLearningAudio } from '@shared/media/audio/use-warm-learning-audio'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type KeyboardEvent, type ReactNode } from 'react'
import { useThemePreference } from '@app/theme-preference-context'
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  ListTree,
  Presentation,
  Trash2,
} from 'lucide-react'
import type { LearningLessonDetail } from '@services/firebase/learning'
import { useDashboardState } from '@features/dashboard'
import { LearningAudioWave } from '@features/learning/shared/online-book-content/primitives'
import { selectPreferredLearningLessonDetails } from '@shared/media/audio/learning-audio-preference'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  SafeHtml,
  LmsButton,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  ScrollArea,
  ScrollBar,
} from '@shared/ui'
import { cn } from '@shared/ui/lib/utils'
import {
  buildTeachingPresentationDeck,
  type TeachingPresentationCard,
  type TeachingPresentationCardsSlide,
  type TeachingPresentationImageSlide,
  type TeachingPresentationMultipleChoiceSlide,
  type TeachingPresentationSlide,
  type TeachingPresentationTextSlide,
  type TeachingPresentationTitleSlide,
} from './ebook-presentation-model'
import { getTeachingPresentationTemplateDefinition } from './presentation-template-registry'
import {
  createTeachingPresentationChannel,
  createTeachingPresentationSenderId,
  createTeachingPresentationSessionId,
  type TeachingPresentationMessage,
  type TeachingPresentationSnapshot,
  type TeachingPresentationView,
} from './presentation-session'
import {
  getTeachingPresentationTheme,
  getTeachingPresentationStyle,
  type TeachingPresentationAppearance,
  type TeachingPresentationTheme,
  type TeachingPresentationStyleId,
} from './presentation-styles'
import { useStudyTimeTracker } from '@features/learning/shared/useStudyTimeTracker'
import './teaching-presentation.css'

interface TeachingPresentationScreenProps {
  readonly blocks: readonly LearningLessonDetail[]
  readonly lessonId: string
  readonly lessonTitle: string
  readonly locale: string
  readonly mode?: TeachingPresentationView
  readonly onExit: () => void
  readonly sessionId?: string
}

interface TeachingPresentationAudioPlayback {
  readonly label: string
  readonly slideId: string | null
  readonly source: string
  readonly status: 'error' | 'playing'
}

type PresentationThemeStyle = CSSProperties & Record<`--presentation-${string}`, string | number>

function getPresentationThemeStyle(theme: TeachingPresentationTheme): PresentationThemeStyle {
  return {
    '--presentation-accent': theme.accent,
    '--presentation-accent-contrast': theme.accentContrast,
    '--presentation-border': theme.border,
    '--presentation-muted': theme.textMuted,
    '--presentation-surface': theme.surface,
    '--presentation-surface-raised': theme.surfaceRaised,
    '--presentation-text': theme.text,
  }
}

function getInitialSnapshot(
  deck: ReturnType<typeof buildTeachingPresentationDeck>,
): TeachingPresentationSnapshot {
  return {
    customSlides: [],
    currentSlideId: deck.slides[0]?.id ?? null,
    hiddenSlideIds: [],
    orderedSlideIds: deck.slides.map((slide) => slide.id),
    showAnswer: false,
    showTranslation: false,
    styleId: 'classroom',
  }
}

function ProjectorSurface({
  lessonTitle,
  showAnswer,
  showTranslation,
  slide,
  styleId,
  isControllerPreview = false,
  isStandalone = false,
  showLessonTitle = true,
  onToggleAudio,
  playingAudioSource,
  audioErrorSource,
}: {
  readonly lessonTitle: string
  readonly showAnswer: boolean
  readonly showTranslation: boolean
  readonly slide: TeachingPresentationSlide | undefined
  readonly styleId: TeachingPresentationStyleId
  readonly isControllerPreview?: boolean
  readonly isStandalone?: boolean
  readonly showLessonTitle?: boolean
  readonly onToggleAudio?: (source: string, label: string) => void
  readonly playingAudioSource?: string | null
  readonly audioErrorSource?: string | null
}) {
  const ui = useLearningUiTranslation()
  const style = getTeachingPresentationStyle(styleId)
  const { resolvedTheme } = useThemePreference()
  const appearance: TeachingPresentationAppearance = resolvedTheme === 'dark' ? 'DARK' : 'LIGHT'
  const theme = getTeachingPresentationTheme(styleId, appearance)

  if (!slide) {
    return (
      <div aria-label={ui("Projector slide")} className={cn('teachingPresentationProjectorSurface', 'teachingPresentationProjectorSurface--empty', isStandalone && 'teachingPresentationProjectorSurface--standalone', isControllerPreview && 'teachingPresentationProjectorSurface--preview')} data-presentation-view="projector" style={getPresentationThemeStyle(theme)}>
        <h2 className="teachingPresentationTitle">{ui("No presentation slides available.")}</h2>
      </div>
    )
  }

  const templateDefinition = getTeachingPresentationTemplateDefinition(slide.slideTemplate)

  return (
    <div aria-label={ui("Projector slide")} className={cn('teachingPresentationProjectorSurface', isStandalone && 'teachingPresentationProjectorSurface--standalone', isControllerPreview && 'teachingPresentationProjectorSurface--preview')} data-presentation-slide-template={templateDefinition.slideTemplate} data-presentation-view="projector" style={getPresentationThemeStyle(theme)}>
      {showLessonTitle ? <p className="teachingPresentationProjectorSurface__lessonTitle">{lessonTitle}</p> : null}
      <ProjectorSlide audioErrorSource={audioErrorSource} density={style.density} interactive={isControllerPreview} isThumbnail={false} onToggleAudio={onToggleAudio} playingAudioSource={playingAudioSource} showAnswer={showAnswer} showTranslation={showTranslation} slide={slide} theme={theme} />
    </div>
  )
}

interface PresentationSlideRendererProps {
  readonly density: 'comfortable' | 'compact'
  readonly interactive: boolean
  readonly isThumbnail: boolean
  readonly onToggleAudio?: (source: string, label: string) => void
  readonly playingAudioSource?: string | null
  readonly audioErrorSource?: string | null
  readonly showAnswer: boolean
  readonly showTranslation: boolean
  readonly theme: TeachingPresentationTheme
}

interface TemplateAudioContextValue {
  readonly audioErrorSource?: string | null
  readonly interactive: boolean
  readonly onToggleAudio?: (source: string, label: string) => void
  readonly playingAudioSource?: string | null
}

const TemplateAudioContext = createContext<TemplateAudioContextValue>({ interactive: false })

function SlideTemplateTitle({ slide, isThumbnail, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationTitleSlide }) {
  return <div className={cn('teachingPresentationTemplate', 'teachingPresentationTemplate--centered', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}><h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--hero', isThumbnail && 'teachingPresentationTitle--thumbnail')}>{slide.title}</h2></div>
}

function SlideTemplateText({ slide, isThumbnail, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationTextSlide }) {
  return (
    <div className={cn('teachingPresentationTemplate', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>
      {slide.title ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}
      {slide.image ? <img alt={slide.title ?? 'Lesson illustration'} className={cn('teachingPresentationImage', isThumbnail && 'teachingPresentationImage--thumbnail')} onError={(event) => { event.currentTarget.style.visibility = 'hidden' }} src={slide.image} /> : null}
      <div className={cn('teachingPresentationText', isThumbnail && 'teachingPresentationText--thumbnail')}><SafeHtml html={slide.html} /></div>
    </div>
  )
}

function SlideTemplateImage({ slide, isThumbnail, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationImageSlide }) {
  return <div className={cn('teachingPresentationTemplate', 'teachingPresentationTemplate--image', 'teachingPresentationTemplate--centered', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}><img alt={slide.caption ?? slide.title ?? 'Lesson illustration'} className={cn('teachingPresentationImage', isThumbnail && 'teachingPresentationImage--thumbnail')} onError={(event) => { event.currentTarget.style.visibility = 'hidden' }} src={slide.image} />{slide.caption ? <p className={cn('teachingPresentationCaption', isThumbnail && 'teachingPresentationCaption--thumbnail')}>{slide.caption}</p> : null}</div>
}

function SlideTemplateMultipleChoice({ slide, isThumbnail, showAnswer, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationMultipleChoiceSlide }) {
  return <div className={cn('teachingPresentationTemplate', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>{slide.title && slide.title !== slide.prompt ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}<p className={cn('teachingPresentationPrompt', isThumbnail && 'teachingPresentationPrompt--thumbnail')}>{slide.prompt}</p><div className={cn('teachingPresentationChoices', isThumbnail && 'teachingPresentationChoices--thumbnail')}>{slide.choices.map((choice, index) => <div className={cn('teachingPresentationChoice', showAnswer && choice.isCorrect && 'teachingPresentationChoice--correct', isThumbnail && 'teachingPresentationChoice--thumbnail')} key={`${choice.label}-${index}`} style={{ '--presentation-border': theme.border, '--presentation-accent': theme.accent } as CSSProperties}><span className="teachingPresentationChoice__letter">{String.fromCharCode(65 + index)}</span><span>{showAnswer && choice.isCorrect ? '✓ ' : ''}{choice.label}</span></div>)}</div></div>
}

function TemplateCardImage({ card, isThumbnail, shape = 'circle' }: { readonly card: TeachingPresentationCard; readonly isThumbnail: boolean; readonly shape?: 'circle' | 'rounded' }) {
  if (!card.image) return null

  return <img alt="" className={cn('teachingPresentationCardImage', shape === 'circle' && 'teachingPresentationCardImage--circle', isThumbnail && 'teachingPresentationCardImage--thumbnail')} onError={(event) => { event.currentTarget.style.visibility = 'hidden' }} src={card.image} />
}

function TemplateCardAudioButton({ audioOutsideCard = false, card, children, hideAudioControl = false, interactive, onToggleAudio, playingAudioSource, audioErrorSource }: { readonly audioOutsideCard?: boolean; readonly card: TeachingPresentationCard; readonly children: ReactNode; readonly hideAudioControl?: boolean; readonly interactive: boolean; readonly onToggleAudio?: (source: string, label: string) => void; readonly playingAudioSource?: string | null; readonly audioErrorSource?: string | null }) {
  const ui = useLearningUiTranslation()
  const isPlaying = Boolean(card.audio && card.audio === playingAudioSource)
  const hasError = Boolean(card.audio && card.audio === audioErrorSource)

  if (!interactive || !card.audio || !onToggleAudio) {
    return <>{children}</>
  }

  const audioControl = <span className={cn('teachingPresentationAudioControl', isPlaying && 'teachingPresentationAudioControl--playing')}>
      <LearningAudioWave isPlaying={isPlaying} size={16} />
      {!audioOutsideCard ? <span className="teachingPresentationAudioLabel">{hasError ? ui("Audio unavailable") : isPlaying ? ui("Playing") : ui("Play audio")}</span> : null}
    </span>

  return <button aria-label={isPlaying ? ui('Pause {{label}}', { label: card.primary }) : ui('Play {{label}}', { label: card.primary })} aria-pressed={isPlaying} className={cn('teachingPresentationAudioButton', audioOutsideCard && 'teachingPresentationAudioButton--outside')} data-align={audioOutsideCard && card.isRight ? 'right' : 'left'} onClick={() => { onToggleAudio(card.audio as string, card.primary) }} type="button">
    {audioOutsideCard ? children : <span className="teachingPresentationAudioButton__content">{children}</span>}
    {!hideAudioControl ? audioControl : null}
  </button>
}

function TemplateCardText({ audioEnabled = true, card, compact = false, isThumbnail, showTranslation, theme }: { readonly audioEnabled?: boolean; readonly card: TeachingPresentationCard; readonly compact?: boolean; readonly isThumbnail: boolean; readonly showTranslation: boolean; readonly theme: TeachingPresentationTheme }) {
  const audioContext = useContext(TemplateAudioContext)
  const content = <><p className={cn('teachingPresentationCardText', isThumbnail && 'teachingPresentationCardText--thumbnail', compact && 'teachingPresentationCardText--compact')} style={getPresentationThemeStyle(theme)}>{card.primary}</p>{showTranslation && card.secondary ? <p className={cn('teachingPresentationCardTranslation', isThumbnail && 'teachingPresentationCardTranslation--thumbnail', compact && 'teachingPresentationCardTranslation--compact')} style={getPresentationThemeStyle(theme)}>{card.secondary}</p> : null}</>

  if (!audioEnabled) {
    return content
  }

  return <TemplateCardAudioButton audioErrorSource={audioContext.audioErrorSource} card={card} interactive={audioContext.interactive} onToggleAudio={audioContext.onToggleAudio} playingAudioSource={audioContext.playingAudioSource}>{content}</TemplateCardAudioButton>
}

function SlideTemplateConversation({ slide, ...props }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationCardsSlide }) {
  const { isThumbnail, showTranslation, theme } = props
  const audioContext = useContext(TemplateAudioContext)
  const compact = !isThumbnail && slide.cards.length > 4
  return <div className={cn('teachingPresentationTemplate', 'teachingPresentationTemplate--fill', compact && 'teachingPresentationTemplate--compact', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>
    {slide.title ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}
    <div className={cn('teachingPresentationConversationGrid', isThumbnail && 'teachingPresentationConversationGrid--thumbnail')}>
      {slide.image ? <div className="teachingPresentationConversationImage"><img alt="" onError={(event) => { event.currentTarget.style.visibility = 'hidden' }} src={slide.image} /></div> : <div aria-hidden="true" />}
      <div className="teachingPresentationConversationCards">{slide.cards.map((card, index) => { const isPlaying = Boolean(card.audio && card.audio === audioContext.playingAudioSource); const hasError = Boolean(card.audio && card.audio === audioContext.audioErrorSource); const hasInteractiveAudio = Boolean(audioContext.interactive && card.audio && audioContext.onToggleAudio); return <div className={cn('teachingPresentationConversationRow', card.isRight && 'teachingPresentationConversationRow--right')} key={`${card.primary}-${index}`}><div className={cn('teachingPresentationConversationItem', card.isRight && 'teachingPresentationConversationItem--right')}><TemplateCardImage card={card} isThumbnail={isThumbnail} /><TemplateCardAudioButton audioErrorSource={audioContext.audioErrorSource} audioOutsideCard card={card} hideAudioControl={Boolean(card.speaker)} interactive={audioContext.interactive} onToggleAudio={audioContext.onToggleAudio} playingAudioSource={audioContext.playingAudioSource}><div className={cn('teachingPresentationBubble', card.isRight && 'teachingPresentationBubble--right', isThumbnail && 'teachingPresentationBubble--thumbnail')} style={getPresentationThemeStyle(theme)}>{card.speaker ? <div className={cn('teachingPresentationSpeaker', isThumbnail && 'teachingPresentationSpeaker--thumbnail')}><span className="teachingPresentationSpeakerName">{card.speaker}</span>{hasInteractiveAudio ? <span className={cn('teachingPresentationSpeakerAudio', hasError && 'teachingPresentationSpeakerAudio--error', isPlaying && 'teachingPresentationSpeakerAudio--playing')}><LearningAudioWave isPlaying={isPlaying} size={isThumbnail ? 11 : 16} /></span> : null}</div> : null}<TemplateCardText audioEnabled={false} card={card} compact={compact} isThumbnail={isThumbnail} showTranslation={showTranslation} theme={theme} /></div></TemplateCardAudioButton></div></div> })}</div>
    </div>
  </div>
}

function SlideTemplateVocabularyImage({ slide, ...props }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationCardsSlide }) {
  const { density, isThumbnail, showTranslation, theme } = props
  return <div className={cn('teachingPresentationTemplate', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>{slide.title ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}<div className={cn('teachingPresentationCardGrid', isThumbnail && 'teachingPresentationCardGrid--thumbnail', density === 'compact' && 'teachingPresentationCardGrid--compact')}>{slide.cards.map((card, index) => <div className={cn('teachingPresentationCard', isThumbnail && 'teachingPresentationCard--thumbnail')} key={`${card.primary}-${index}`}><TemplateCardImage card={card} isThumbnail={isThumbnail} shape="rounded" /><TemplateCardText card={card} isThumbnail={isThumbnail} showTranslation={showTranslation} theme={theme} /></div>)}</div></div>
}

function SlideTemplateVocabulary({ slide, isThumbnail, showTranslation, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationCardsSlide }) {
  return <div className={cn('teachingPresentationTemplate', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>{slide.title ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}{slide.cards.map((card, index) => <div className="teachingPresentationVocabularyRow" key={`${card.primary}-${index}`}><div><TemplateCardText card={card} isThumbnail={isThumbnail} showTranslation={false} theme={theme} />{card.type ? <span className="teachingPresentationVocabularyType">{card.type}</span> : null}</div>{showTranslation && card.secondary ? <p className="teachingPresentationCardTranslation">{card.secondary}</p> : null}</div>)}</div>
}

function SlideTemplateVocabularyCompact({ slide, isThumbnail, showTranslation, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationCardsSlide }) {
  return <div className={cn('teachingPresentationTemplate', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>{slide.title ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}<div className={cn('teachingPresentationCompactGrid', isThumbnail && 'teachingPresentationCompactGrid--thumbnail')}>{slide.cards.map((card, index) => <div className="teachingPresentationCompactCard" key={`${card.primary}-${index}`}><TemplateCardText card={card} isThumbnail={isThumbnail} showTranslation={showTranslation} theme={theme} /></div>)}</div></div>
}

function SlideTemplateStructure({ slide, isThumbnail, showTranslation, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationCardsSlide }) {
  return <div className={cn('teachingPresentationTemplate', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>{slide.title ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}{slide.cards.map((card, index) => <div className="teachingPresentationStructureCard" key={`${card.primary}-${index}`}><span className="teachingPresentationStructureBadge">{card.type?.slice(0, 1).toUpperCase() ?? String(index + 1)}</span><div><TemplateCardText card={card} isThumbnail={isThumbnail} showTranslation={showTranslation} theme={theme} /></div></div>)}</div>
}

function SlideTemplateList({ slide, isThumbnail, showTranslation, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationCardsSlide }) {
  return <div className={cn('teachingPresentationTemplate', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>{slide.title ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}{slide.cards.map((card, index) => <div className="teachingPresentationListRow" key={`${card.primary}-${index}`}><span className="teachingPresentationListIndex">{index + 1}.</span><TemplateCardText card={card} isThumbnail={isThumbnail} showTranslation={showTranslation} theme={theme} /></div>)}</div>
}

function SlideTemplateHighlighter({ slide, isThumbnail, showTranslation, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationCardsSlide }) {
  return <div className={cn('teachingPresentationTemplate', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>{slide.title ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}{slide.cards.map((card, index) => <div className="teachingPresentationHighlightCard" key={`${card.primary}-${index}`}><TemplateCardText card={card} isThumbnail={isThumbnail} showTranslation={showTranslation} theme={theme} /></div>)}</div>
}

function SlideTemplatePronunciation({ slide, isThumbnail, showTranslation, theme }: PresentationSlideRendererProps & { readonly slide: TeachingPresentationCardsSlide }) {
  return <div className={cn('teachingPresentationTemplate', 'teachingPresentationTemplate--centered', isThumbnail && 'teachingPresentationTemplate--thumbnail')} style={getPresentationThemeStyle(theme)}>{slide.title ? <h2 className={cn('teachingPresentationTitle', 'teachingPresentationTitle--section', isThumbnail && 'teachingPresentationTitle--thumbnailSection')}>{slide.title}</h2> : null}<div className="teachingPresentationPronunciationCards">{slide.cards.map((card, index) => <div className="teachingPresentationPronunciationCard" key={`${card.primary}-${index}`}><TemplateCardText card={card} isThumbnail={isThumbnail} showTranslation={showTranslation} theme={theme} /></div>)}</div></div>
}

function ProjectorSlide({ audioErrorSource, interactive, onToggleAudio, playingAudioSource, showAnswer, showTranslation, slide, density, isThumbnail = false, theme }: { readonly audioErrorSource?: string | null; readonly interactive: boolean; readonly onToggleAudio?: (source: string, label: string) => void; readonly playingAudioSource?: string | null; readonly showAnswer: boolean; readonly showTranslation: boolean; readonly slide: TeachingPresentationSlide; readonly density: 'comfortable' | 'compact'; readonly isThumbnail?: boolean; readonly theme: TeachingPresentationTheme }) {
  const ui = useLearningUiTranslation()
  const props = { audioErrorSource, density, interactive, isThumbnail, onToggleAudio, playingAudioSource, showAnswer, showTranslation, theme }
  const audioContext = { audioErrorSource, interactive, onToggleAudio, playingAudioSource }
  let renderedSlide: ReactNode

  if (slide.kind === 'title' && slide.slideTemplate === 'title') renderedSlide = <SlideTemplateTitle {...props} slide={slide} />
  else if (slide.kind === 'text' && slide.slideTemplate === 'text') renderedSlide = <SlideTemplateText {...props} slide={slide} />
  else if (slide.kind === 'image' && slide.slideTemplate === 'image') renderedSlide = <SlideTemplateImage {...props} slide={slide} />
  else if (slide.kind === 'multiple_choice' && slide.slideTemplate === 'multiple-choice') renderedSlide = <SlideTemplateMultipleChoice {...props} slide={slide} />
  else if (slide.kind !== 'cards') renderedSlide = <p className="teachingPresentationTextMuted" style={{ color: theme.textMuted }}>{ui("Slide content unavailable.")}</p>
  else {
    switch (slide.slideTemplate) {
      case 'conversation': renderedSlide = <SlideTemplateConversation {...props} slide={slide} />; break
      case 'vocabulary-image': renderedSlide = <SlideTemplateVocabularyImage {...props} slide={slide} />; break
      case 'vocabulary': renderedSlide = <SlideTemplateVocabulary {...props} slide={slide} />; break
      case 'vocabulary-compact': renderedSlide = <SlideTemplateVocabularyCompact {...props} slide={slide} />; break
      case 'structure': renderedSlide = <SlideTemplateStructure {...props} slide={slide} />; break
      case 'list': renderedSlide = <SlideTemplateList {...props} slide={slide} />; break
      case 'highlighter': renderedSlide = <SlideTemplateHighlighter {...props} slide={slide} />; break
      case 'pronunciation': renderedSlide = <SlideTemplatePronunciation {...props} slide={slide} />; break
      default: renderedSlide = <p className="teachingPresentationTextMuted" style={{ color: theme.textMuted }}>{ui("Slide content unavailable.")}</p>
    }
  }

  return <TemplateAudioContext.Provider value={audioContext}>{renderedSlide}</TemplateAudioContext.Provider>
}

function SlideCanvasThumbnail({ slide, styleId, theme }: { readonly slide: TeachingPresentationSlide; readonly styleId: TeachingPresentationStyleId; readonly theme: TeachingPresentationTheme }) {
  const templateDefinition = getTeachingPresentationTemplateDefinition(slide.slideTemplate)
  const style = getTeachingPresentationStyle(styleId)

  return (
    <div aria-hidden="true" className="teachingPresentationSlideCanvas" data-presentation-slide-canvas data-presentation-slide-template={templateDefinition.slideTemplate} style={getPresentationThemeStyle(theme)}>
      <ProjectorSlide density={style.density} interactive={false} isThumbnail showAnswer={false} showTranslation={false} slide={slide} theme={theme} />
    </div>
  )
}

function getSlideLabel(slide: TeachingPresentationSlide) {
  if (slide.kind === 'multiple_choice') {
    return slide.title ?? slide.prompt
  }

  return slide.title ?? slide.kind
}

function getPresentationButtonStyle(theme: TeachingPresentationTheme, foreground = theme.text, hoverBackground: string | null | undefined = theme.surfaceRaised, hoverForeground = foreground): CSSProperties {
  return {
    '--lms-button-fg': foreground,
    ...(hoverBackground ? { '--lms-button-hover-bg': hoverBackground, '--lms-button-hover-fg': hoverForeground } : {}),
  } as CSSProperties
}

function SlideThumbnail({
  isDragging,
  isDropTarget,
  index,
  isActive,
  isHidden,
  onDuplicate,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onMove,
  onRemove,
  onSelect,
  onToggleHidden,
  slide,
  styleId,
  theme,
}: {
  readonly isDragging: boolean
  readonly isDropTarget: boolean
  readonly index: number
  readonly isActive: boolean
  readonly isHidden: boolean
  readonly onDuplicate: (slideId: string) => void
  readonly onDragEnd: () => void
  readonly onDragOver: (slideId: string) => void
  readonly onDragStart: (slideId: string) => void
  readonly onDrop: (slideId: string) => void
  readonly onMove: (slideId: string, direction: -1 | 1) => void
  readonly onRemove: (slideId: string) => void
  readonly onSelect: (slideId: string) => void
  readonly onToggleHidden: (slideId: string) => void
  readonly slide: TeachingPresentationSlide
  readonly styleId: TeachingPresentationStyleId
  readonly theme: TeachingPresentationTheme
}) {
  const ui = useLearningUiTranslation()
  const sourceLabel = getSlideLabel(slide)
  const label = !slide.title && ['cards', 'text', 'image'].includes(sourceLabel)
    ? ui(sourceLabel as 'cards' | 'text' | 'image')
    : sourceLabel

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <LmsButton
          aria-current={isActive ? 'step' : undefined}
          aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
          aria-label={ui('Select slide {{index}}: {{label}}. Drag to reorder, or use Alt plus Arrow Up or Arrow Down.', { index: index + 1, label })}
          className={cn(
            'h-auto min-h-0 w-full flex-col items-stretch justify-start gap-1.5 whitespace-normal rounded-lg border border-transparent border-l-[3px] border-l-transparent p-1.5 text-left transition-colors focus-visible:outline-[3px] focus-visible:outline-[color-mix(in_srgb,var(--lms-color-brand-action)_42%,transparent)] focus-visible:outline-offset-2',
            isDragging && 'cursor-grabbing opacity-45',
            isHidden && 'cursor-default opacity-55',
            !isHidden && !isDragging && 'cursor-pointer',
          )}
          draggable={!isHidden}
          style={{ ...getPresentationButtonStyle(theme), backgroundColor: isActive ? `color-mix(in srgb, ${theme.accent} 10%, transparent)` : isDropTarget ? `color-mix(in srgb, ${theme.accent} 16%, transparent)` : undefined, borderColor: isActive || isDropTarget ? `color-mix(in srgb, ${theme.accent} 45%, transparent)` : 'transparent', borderLeftColor: isActive || isDropTarget ? theme.accent : 'transparent' }}
          variant="ghost"
          onClick={() => { onSelect(slide.id) }}
          onDragEnd={onDragEnd}
          onDragOver={(event: DragEvent<HTMLButtonElement>) => {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
            onDragOver(slide.id)
          }}
          onDragStart={(event: DragEvent<HTMLButtonElement>) => {
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', slide.id)
            onDragStart(slide.id)
          }}
          onDrop={(event: DragEvent<HTMLButtonElement>) => {
            event.preventDefault()
            onDrop(event.dataTransfer.getData('text/plain') || slide.id)
          }}
          onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
            if (!event.altKey || !['ArrowUp', 'ArrowDown'].includes(event.key)) {
              return
            }

            event.preventDefault()
            onMove(slide.id, event.key === 'ArrowUp' ? -1 : 1)
          }}
          type="button"
        >
          <div className="teachingPresentationThumbnailMeta">
            <span className="teachingPresentationThumbnailIndex" style={{ color: isActive ? theme.accent : theme.textMuted }}>{String(index + 1).padStart(2, '0')}</span>
            {isHidden ? <span className="teachingPresentationThumbnailHidden" style={{ color: theme.textMuted }}>{ui("Hidden")}</span> : null}
          </div>
          <SlideCanvasThumbnail slide={slide} styleId={styleId} theme={theme} />
          <span className="teachingPresentationThumbnailLabel" style={{ color: isHidden ? theme.textMuted : theme.text }}>{label}</span>
        </LmsButton>
      </ContextMenuTrigger>
      <ContextMenuContent aria-label={ui('Actions for slide {{index}}', { index: index + 1 })}>
        <ContextMenuItem onSelect={() => { onToggleHidden(slide.id) }}>
          {isHidden ? <Eye aria-hidden="true" className="size-4" /> : <EyeOff aria-hidden="true" className="size-4" />}
          <span>{isHidden ? ui("Show slide") : ui("Hide slide")}</span>
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => { onDuplicate(slide.id) }}>
          <Copy aria-hidden="true" className="size-4" />
          <span>{ui("Duplicate slide")}</span>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem className="text-[var(--lms-color-danger)] focus:bg-[color-mix(in_srgb,var(--lms-color-danger)_12%,transparent)] focus:text-[var(--lms-color-danger)]" onSelect={() => { onRemove(slide.id) }}>
          <Trash2 aria-hidden="true" className="size-4" />
          <span>{ui("Remove slide")}</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}

function ControllerOutline({
  activeSlideId,
  deck,
  draggedSlideId,
  dragOverSlideId,
  hiddenSlideIds,
  onDuplicate,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onMove,
  onRemove,
  onSelect,
  onToggleHidden,
  orderedSlideIds,
  slides,
  styleId,
  theme,
}: {
  readonly activeSlideId: string | null
  readonly deck: ReturnType<typeof buildTeachingPresentationDeck>
  readonly draggedSlideId: string | null
  readonly dragOverSlideId: string | null
  readonly hiddenSlideIds: readonly string[]
  readonly onDuplicate: (slideId: string) => void
  readonly onDragEnd: () => void
  readonly onDragOver: (slideId: string) => void
  readonly onDragStart: (slideId: string) => void
  readonly onDrop: (slideId: string) => void
  readonly onMove: (slideId: string, direction: -1 | 1) => void
  readonly onRemove: (slideId: string) => void
  readonly onSelect: (slideId: string) => void
  readonly onToggleHidden: (slideId: string) => void
  readonly orderedSlideIds: readonly string[]
  readonly slides: readonly TeachingPresentationSlide[]
  readonly styleId: TeachingPresentationStyleId
  readonly theme: TeachingPresentationTheme
}) {
  const ui = useLearningUiTranslation()
  const sectionTitleById = new Map(deck.sections.map((section) => [section.id, section.title] as const))
  const sectionBySlideId = new Map(slides.map((slide) => [slide.id, sectionTitleById.get(slide.sectionId) ?? ui("Lesson")] as const))

  return (
    <div className="teachingPresentationOutline" data-presentation-outline>
      {orderedSlideIds.map((slideId, index) => {
        const slide = slides.find((entry) => entry.id === slideId)

        if (!slide) {
          return null
        }

        const isHidden = hiddenSlideIds.includes(slideId)
        const previousSection = sectionBySlideId.get(orderedSlideIds[index - 1])

        return (
          <div key={slideId}>
            {index === 0 || sectionBySlideId.get(slideId) !== previousSection ? <div className={cn('teachingPresentationOutlineSection', index === 0 && 'teachingPresentationOutlineSection--first')} style={getPresentationThemeStyle(theme)}><span className="teachingPresentationOutlineSectionMark" /><span className="teachingPresentationSectionLabel">{sectionBySlideId.get(slideId) ?? ui("Lesson")}</span></div> : null}
            <SlideThumbnail index={index} isActive={activeSlideId === slideId} isDragging={draggedSlideId === slideId} isDropTarget={dragOverSlideId === slideId && draggedSlideId !== slideId} isHidden={isHidden} onDuplicate={onDuplicate} onDragEnd={onDragEnd} onDragOver={onDragOver} onDragStart={onDragStart} onDrop={onDrop} onMove={onMove} onRemove={onRemove} onSelect={onSelect} onToggleHidden={onToggleHidden} slide={slide} styleId={styleId} theme={theme} />
          </div>
        )
      })}
    </div>
  )
}

interface ControllerSlideRailProps {
  readonly children: ReactNode
  readonly currentTheme: TeachingPresentationTheme
  readonly isRailOpen: boolean
  readonly railId: string
  readonly visibleSlideCount: number
}

function ControllerSlideRail({ children, currentTheme, isRailOpen, railId, visibleSlideCount }: ControllerSlideRailProps) {
  const ui = useLearningUiTranslation()
  return (
    <aside className={cn('teachingPresentationRail', isRailOpen && 'teachingPresentationRail--open')} id={railId} data-presentation-view="controller" style={getPresentationThemeStyle(currentTheme)}>
      <div className="teachingPresentationRailContent">
        <div className="teachingPresentationRailHeader">
          <div className="teachingPresentationRailTitle"><ListTree aria-hidden="true" className="size-5" /><span>{ui("Slides")}</span></div>
          <span className="teachingPresentationRailCount">{ui('{{count}} visible', { count: visibleSlideCount })}</span>
        </div>
        <ScrollArea className="min-h-0 flex-1 pr-0.5">
          {children}
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </div>
    </aside>
  )
}

interface ControllerStageProps {
  readonly children: ReactNode
  readonly currentTheme: TeachingPresentationTheme
  readonly footer: ReactNode
}

function ControllerStage({ children, currentTheme, footer }: ControllerStageProps) {
  const ui = useLearningUiTranslation()
  return (
    <section className="teachingPresentationStageShell" aria-label={ui("Presentation stage")} style={getPresentationThemeStyle(currentTheme)}>
      <div className="teachingPresentationStage" data-presentation-stage>
        {children}
      </div>
      <footer className="teachingPresentationFooter">
        {footer}
      </footer>
    </section>
  )
}

export function TeachingPresentationScreen({
  blocks,
  lessonId,
  lessonTitle,
  locale,
  mode = 'controller',
  onExit,
  sessionId,
}: TeachingPresentationScreenProps) {
  const ui = useLearningUiTranslation()
  useStudyTimeTracker({ surface: 'teaching-presentation' })
  const { state } = useDashboardState()
  const accentDefault = state.myUser?.accent_default
  const preferredBlocks = useMemo(
    () => selectPreferredLearningLessonDetails(blocks, accentDefault),
    [accentDefault, blocks],
  )
  const deck = useMemo(() => buildTeachingPresentationDeck({ blocks: preferredBlocks, lessonId, locale, title: lessonTitle }), [preferredBlocks, lessonId, lessonTitle, locale])
  const initialSnapshot = useMemo(() => getInitialSnapshot(deck), [deck])
  const [customSlides, setCustomSlides] = useState<readonly TeachingPresentationSlide[]>(initialSnapshot.customSlides ?? [])
  const [orderedSlideIds, setOrderedSlideIds] = useState<readonly string[]>(initialSnapshot.orderedSlideIds)
  const [hiddenSlideIds, setHiddenSlideIds] = useState<readonly string[]>(initialSnapshot.hiddenSlideIds)
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(initialSnapshot.currentSlideId)
  const styleId = initialSnapshot.styleId
  const [showAnswer, setShowAnswer] = useState(initialSnapshot.showAnswer)
  const [showTranslation, setShowTranslation] = useState(initialSnapshot.showTranslation)
  const [remoteSnapshot, setRemoteSnapshot] = useState<TeachingPresentationSnapshot | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isStopped, setIsStopped] = useState(false)
  const [audioPlayback, setAudioPlayback] = useState<TeachingPresentationAudioPlayback | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [popupStatus, setPopupStatus] = useState<'idle' | 'open' | 'blocked'>('idle')
  const [draggedSlideId, setDraggedSlideId] = useState<string | null>(null)
  const [dragOverSlideId, setDragOverSlideId] = useState<string | null>(null)
  const [activeSessionId] = useState(() => sessionId ?? createTeachingPresentationSessionId())
  const [isRailOpen, setIsRailOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 900)
  const isChannelAvailable = typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
  const senderIdRef = useRef(createTeachingPresentationSenderId())
  const channelRef = useRef<BroadcastChannel | null>(null)
  const snapshotRef = useRef(initialSnapshot)
  const lastControllerMessageAtRef = useRef(0)

  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const previousHtmlOverflow = html.style.overflow
    const previousBodyOverflow = body.style.overflow

    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    body.classList.add('lms-teaching-presentation-active')

    return () => {
      html.style.overflow = previousHtmlOverflow
      body.style.overflow = previousBodyOverflow
      body.classList.remove('lms-teaching-presentation-active')
    }
  }, [])

  const localSnapshot = useMemo<TeachingPresentationSnapshot>(() => ({ customSlides, currentSlideId: selectedSlideId, hiddenSlideIds, orderedSlideIds, showAnswer, showTranslation, styleId }), [customSlides, hiddenSlideIds, orderedSlideIds, selectedSlideId, showAnswer, showTranslation, styleId])
  const currentSnapshot = mode === 'projector' ? remoteSnapshot ?? initialSnapshot : localSnapshot
  const { resolvedTheme } = useThemePreference()
  const presentationAppearance: TeachingPresentationAppearance = resolvedTheme === 'dark' ? 'DARK' : 'LIGHT'
  const currentTheme = getTeachingPresentationTheme(currentSnapshot.styleId, presentationAppearance)
  const sessionSlides = useMemo(() => {
    const snapshotSlides = mode === 'projector' ? currentSnapshot.customSlides ?? [] : customSlides
    return [...deck.slides, ...snapshotSlides]
  }, [currentSnapshot.customSlides, customSlides, deck.slides, mode])
  const visibleSlideIds = useMemo(() => currentSnapshot.orderedSlideIds.filter((slideId) => !currentSnapshot.hiddenSlideIds.includes(slideId) && sessionSlides.some((slide) => slide.id === slideId)), [currentSnapshot.hiddenSlideIds, currentSnapshot.orderedSlideIds, sessionSlides])
  const activeSlideId = visibleSlideIds.includes(currentSnapshot.currentSlideId ?? '') ? currentSnapshot.currentSlideId : visibleSlideIds[0] ?? null
  const activeSlide = sessionSlides.find((slide) => slide.id === activeSlideId)
  useWarmLearningAudio(mode !== 'projector' && activeSlide?.kind === 'cards' ? activeSlide.cards.flatMap(card => card.audio ? [card.audio] : []) : [])
  const activeSlideIndex = activeSlideId ? visibleSlideIds.indexOf(activeSlideId) : -1

  const stopAudio = useCallback(() => {
    audioRef.current?.pause()
    audioRef.current = null
    setAudioPlayback(null)
  }, [])

  const handleToggleAudio = useCallback((source: string, label: string) => {
    if (audioPlayback?.slideId === activeSlideId && audioPlayback.source === source && audioPlayback.status === 'playing') {
      stopAudio()
      return
    }

    audioRef.current?.pause()
    const audio = createCachedLearningAudio(source)
    audioRef.current = audio
    audio.onended = () => {
      if (audioRef.current === audio) {
        audioRef.current = null
        setAudioPlayback(null)
      }
    }
    audio.onerror = () => {
      if (audioRef.current === audio) {
        setAudioPlayback({ label, slideId: activeSlideId, source, status: 'error' })
      }
    }
    setAudioPlayback({ label, slideId: activeSlideId, source, status: 'playing' })
    void audio.play().catch(() => {
      if (audioRef.current === audio) {
        setAudioPlayback({ label, slideId: activeSlideId, source, status: 'error' })
      }
    })
  }, [activeSlideId, audioPlayback, stopAudio])

  useEffect(() => {
    const audio = audioRef.current

    return () => {
      audio?.pause()
      if (audioRef.current === audio) {
        audioRef.current = null
      }
    }
  }, [activeSlideId, mode])

  const activeAudioPlayback = audioPlayback?.slideId === activeSlideId ? audioPlayback : null

  useEffect(() => {
    snapshotRef.current = currentSnapshot
  }, [currentSnapshot])

  useEffect(() => {
    const channel = createTeachingPresentationChannel(activeSessionId)
    channelRef.current = channel
    const senderId = senderIdRef.current

    if (!channel) {
      return undefined
    }

    channel.onmessage = (event: MessageEvent<TeachingPresentationMessage>) => {
      const message = event.data

      if (message.sessionId !== activeSessionId || message.senderId === senderId) {
        return
      }

      if (mode === 'controller' && message.kind === 'hello') {
        setIsConnected(true)
        channel.postMessage({ kind: 'snapshot', senderId, sessionId: activeSessionId, snapshot: snapshotRef.current } satisfies TeachingPresentationMessage)
        return
      }

      if (mode === 'projector' && message.kind === 'snapshot') {
        lastControllerMessageAtRef.current = Date.now()
        setIsConnected(true)
        setIsStopped(false)
        setRemoteSnapshot(message.snapshot)
        return
      }

      if (mode === 'projector' && message.kind === 'stop') {
        setIsStopped(true)
        setIsConnected(false)
      }
    }

    if (mode === 'projector') {
      channel.postMessage({ kind: 'hello', senderId, sessionId: activeSessionId } satisfies TeachingPresentationMessage)
    }

    return () => {
      if (mode === 'controller') {
        channel.postMessage({ kind: 'stop', senderId, sessionId: activeSessionId } satisfies TeachingPresentationMessage)
      }
      channel.close()
      channelRef.current = null
    }
  }, [activeSessionId, mode])

  useEffect(() => {
    if (mode !== 'controller' || !channelRef.current) {
      return
    }

    channelRef.current.postMessage({ kind: 'snapshot', senderId: senderIdRef.current, sessionId: activeSessionId, snapshot: localSnapshot } satisfies TeachingPresentationMessage)
  }, [activeSessionId, localSnapshot, mode])

  useEffect(() => {
    if (mode !== 'controller') {
      return
    }

    const heartbeat = window.setInterval(() => {
      channelRef.current?.postMessage({ kind: 'snapshot', senderId: senderIdRef.current, sessionId: activeSessionId, snapshot: snapshotRef.current } satisfies TeachingPresentationMessage)
    }, 3000)

    return () => {
      window.clearInterval(heartbeat)
    }
  }, [activeSessionId, mode])

  useEffect(() => {
    if (mode !== 'projector') {
      return
    }

    const connectionCheck = window.setInterval(() => {
      if (lastControllerMessageAtRef.current > 0 && Date.now() - lastControllerMessageAtRef.current > 9000) {
        setIsConnected(false)
      }
    }, 3000)

    return () => {
      window.clearInterval(connectionCheck)
    }
  }, [mode])

  useEffect(() => {
    if (mode !== 'controller') {
      return
    }

    const handleBeforeUnload = () => {
      channelRef.current?.postMessage({ kind: 'stop', senderId: senderIdRef.current, sessionId: activeSessionId } satisfies TeachingPresentationMessage)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [activeSessionId, mode])

  const handleSelectSlide = (slideId: string) => {
    if (currentSnapshot.hiddenSlideIds.includes(slideId)) {
      return
    }
    setSelectedSlideId(slideId)
  }

  const handleMoveSlide = (slideId: string, direction: -1 | 1) => {
    const index = orderedSlideIds.indexOf(slideId)
    const nextIndex = index + direction

    if (index < 0 || nextIndex < 0 || nextIndex >= orderedSlideIds.length) {
      return
    }

    const nextOrder = [...orderedSlideIds]
    ;[nextOrder[index], nextOrder[nextIndex]] = [nextOrder[nextIndex], nextOrder[index]]
    setOrderedSlideIds(nextOrder)
  }

  const handleDropSlide = (sourceSlideId: string) => {
    const targetSlideId = dragOverSlideId

    if (!targetSlideId || sourceSlideId === targetSlideId) {
      setDraggedSlideId(null)
      setDragOverSlideId(null)
      return
    }

    setOrderedSlideIds((currentOrder) => {
      const sourceIndex = currentOrder.indexOf(sourceSlideId)
      const targetIndex = currentOrder.indexOf(targetSlideId)

      if (sourceIndex < 0 || targetIndex < 0) {
        return currentOrder
      }

      const nextOrder = [...currentOrder]
      nextOrder.splice(sourceIndex, 1)
      nextOrder.splice(nextOrder.indexOf(targetSlideId), 0, sourceSlideId)
      return nextOrder
    })
    setDraggedSlideId(null)
    setDragOverSlideId(null)
  }

  const handleToggleHidden = (slideId: string) => {
    setHiddenSlideIds((current) => current.includes(slideId) ? current.filter((id) => id !== slideId) : [...current, slideId])
  }

  const handleDuplicateSlide = (slideId: string) => {
    const sourceSlide = sessionSlides.find((slide) => slide.id === slideId)

    if (!sourceSlide) {
      return
    }

    const duplicateId = `${sourceSlide.id}-duplicate-${createTeachingPresentationSenderId()}`
    const duplicateSlide: TeachingPresentationSlide = { ...sourceSlide, id: duplicateId }
    const sourceIndex = orderedSlideIds.indexOf(slideId)

    setCustomSlides((current) => [...current, duplicateSlide])
    setOrderedSlideIds((current) => {
      const nextOrder = [...current]
      nextOrder.splice(sourceIndex + 1, 0, duplicateId)
      return nextOrder
    })
    setSelectedSlideId(duplicateId)
  }

  const handleRemoveSlide = (slideId: string) => {
    const sourceIndex = orderedSlideIds.indexOf(slideId)
    const nextOrder = orderedSlideIds.filter((id) => id !== slideId)
    const nextVisibleSlideIds = nextOrder.filter((id) => !hiddenSlideIds.includes(id))

    setCustomSlides((current) => current.filter((slide) => slide.id !== slideId))
    setHiddenSlideIds((current) => current.filter((id) => id !== slideId))
    setOrderedSlideIds(nextOrder)

    if (selectedSlideId === slideId) {
      setSelectedSlideId(nextVisibleSlideIds[Math.min(Math.max(sourceIndex, 0), nextVisibleSlideIds.length - 1)] ?? null)
    }
  }

  const handleOpenProjector = () => {
    const projectorUrl = new URL(window.location.href)
    projectorUrl.searchParams.set('presentationLessonId', lessonId)
    projectorUrl.searchParams.set('presentationView', 'projector')
    projectorUrl.searchParams.set('presentationSession', activeSessionId)
    const projectorWindow = window.open(
      projectorUrl.toString(),
      `xinchaovietnam-teaching-projector-${activeSessionId}`,
      'popup=yes,width=1280,height=720,resizable=yes,scrollbars=yes',
    )

    if (!projectorWindow) {
      setPopupStatus('blocked')
      return
    }

    setPopupStatus('open')
    projectorWindow.focus()
  }

  const connectionLabel = popupStatus === 'blocked'
    ? ui("Popup blocked · preview only")
    : !isChannelAvailable
      ? ui("Live sync unavailable · preview only")
    : isConnected
      ? ui("Projector connected")
      : popupStatus === 'open'
        ? ui("Projector opening")
        : ui("Preview ready")

  const renderSlideRail = (railId: string) => (
    <ControllerSlideRail currentTheme={currentTheme} isRailOpen={isRailOpen} railId={railId} visibleSlideCount={visibleSlideIds.length}>
      <ControllerOutline activeSlideId={activeSlideId} deck={deck} draggedSlideId={draggedSlideId} dragOverSlideId={dragOverSlideId} hiddenSlideIds={hiddenSlideIds} onDuplicate={handleDuplicateSlide} onDragEnd={() => { setDraggedSlideId(null); setDragOverSlideId(null) }} onDragOver={setDragOverSlideId} onDragStart={setDraggedSlideId} onDrop={handleDropSlide} onMove={handleMoveSlide} onRemove={handleRemoveSlide} onSelect={handleSelectSlide} onToggleHidden={handleToggleHidden} orderedSlideIds={orderedSlideIds} slides={sessionSlides} styleId={styleId} theme={currentTheme} />
    </ControllerSlideRail>
  )

  const renderStage = () => (
    <ControllerStage
      currentTheme={currentTheme}
      footer={(
        <div className="teachingPresentationFooterContent">
          <div className="teachingPresentationFooterActions">
            <LmsButton onClick={() => { setShowTranslation((visible) => !visible) }} size="lg" style={getPresentationButtonStyle(currentTheme, showTranslation ? currentTheme.accentContrast : currentTheme.text, showTranslation ? null : undefined)} variant={showTranslation ? 'accent' : 'outline'}>{showTranslation ? ui("Hide translation") : ui("Reveal translation")}</LmsButton>
            <LmsButton onClick={() => { setShowAnswer((visible) => !visible) }} size="lg" style={getPresentationButtonStyle(currentTheme, showAnswer ? currentTheme.accentContrast : currentTheme.text, showAnswer ? null : undefined)} variant={showAnswer ? 'accent' : 'outline'}>{showAnswer ? ui("Hide answers") : ui("Reveal answers")}</LmsButton>
          </div>
          <div className="teachingPresentationFooterNavigation">
            <LmsButton aria-label={ui("Previous slide")} disabled={activeSlideIndex <= 0} onClick={() => { setSelectedSlideId(visibleSlideIds[activeSlideIndex - 1] ?? null) }} size="icon-lg" style={getPresentationButtonStyle(currentTheme)} variant="ghost"><ChevronLeft aria-hidden="true" className="size-5" /></LmsButton>
            <span aria-live="polite" className="teachingPresentationSlideIndex" role="status">{activeSlideIndex >= 0 ? `${activeSlideIndex + 1} / ${visibleSlideIds.length}` : '0 / 0'}</span>
            <LmsButton aria-label={ui("Next slide")} disabled={activeSlideIndex < 0 || activeSlideIndex >= visibleSlideIds.length - 1} onClick={() => { setSelectedSlideId(visibleSlideIds[activeSlideIndex + 1] ?? null) }} size="icon-lg" variant="primary"><ChevronRight aria-hidden="true" className="size-5" /></LmsButton>
          </div>
        </div>
      )}
    >
      <div className="teachingPresentationStage__content"><ProjectorSurface audioErrorSource={activeAudioPlayback?.status === 'error' ? activeAudioPlayback.source : null} isControllerPreview lessonTitle={lessonTitle} onToggleAudio={handleToggleAudio} playingAudioSource={activeAudioPlayback?.status === 'playing' ? activeAudioPlayback.source : null} showAnswer={showAnswer} showLessonTitle={false} showTranslation={showTranslation} slide={activeSlide} styleId={styleId} /></div>
    </ControllerStage>
  )

  if (mode === 'projector') {
    const projectorTheme = getTeachingPresentationTheme(currentSnapshot.styleId, presentationAppearance)

    return (
      <>
        <main className="teachingPresentationRoot teachingPresentationProjectorRoot" data-presentation-mode="projector" style={getPresentationThemeStyle(projectorTheme)}>
          <div className="teachingPresentationProjectorStage">
            <div className="teachingPresentationProjectorStage__content">
            <ProjectorSurface isStandalone lessonTitle={lessonTitle} showAnswer={currentSnapshot.showAnswer} showTranslation={currentSnapshot.showTranslation} slide={isStopped ? undefined : activeSlide} styleId={currentSnapshot.styleId} />
            </div>
          </div>
          {!isConnected && !isStopped ? <p aria-live="polite" className="teachingPresentationProjectorStatus" role="status">{ui("Waiting for controller…")}</p> : null}
          {isStopped ? <p aria-live="polite" className="teachingPresentationProjectorStatus" role="status">{ui("Presentation ended.")}</p> : null}
        </main>
      </>
    )
  }

  return (
    <>
      <main className="teachingPresentationRoot" data-presentation-mode="controller" style={getPresentationThemeStyle(currentTheme)}>
      <header className="teachingPresentationHeader">
        <div className="teachingPresentationHeaderTitle">
            <LmsButton aria-label={ui("Exit teaching presentation")} onClick={onExit} size="icon-lg" style={getPresentationButtonStyle(currentTheme)} variant="ghost"><ArrowLeft aria-hidden="true" className="size-5" /></LmsButton>
            <h1>{lessonTitle}</h1>
        </div>
        <div className="teachingPresentationHeaderActions">
            <div className="teachingPresentationMobileToggle"><LmsButton aria-controls="teaching-presentation-slide-rail-mobile" aria-expanded={isRailOpen} onClick={() => { setIsRailOpen((open) => !open) }} size="lg" style={getPresentationButtonStyle(currentTheme)} variant="ghost">{isRailOpen ? ui("Hide slides") : ui("Show slides")}</LmsButton></div>
            <div aria-label={ui("Presentation connection status")} aria-live="polite" className="teachingPresentationConnection" role="status">
              <span aria-hidden="true" className={cn('teachingPresentationConnectionDot', popupStatus === 'blocked' && 'teachingPresentationConnectionDot--blocked', isConnected && 'teachingPresentationConnectionDot--connected')} />
              <span className="teachingPresentationConnectionText">{connectionLabel}</span>
            </div>
            <LmsButton leadingIcon={<Presentation aria-hidden="true" className="size-4" />} onClick={handleOpenProjector} size="lg" variant="primary">{ui("Open projector")}</LmsButton>
        </div>
      </header>

      <div className={cn('teachingPresentationMobileWorkspace', isRailOpen && 'teachingPresentationMobileWorkspace--rail-open')}>
        {renderSlideRail('teaching-presentation-slide-rail-mobile')}
        {renderStage()}
      </div>

      <div className="teachingPresentationDesktopWorkspace">
        <ResizablePanelGroup className="min-h-0 w-full flex-1" id="teaching-presentation-workspace" orientation="horizontal">
          <ResizablePanel defaultSize={272} maxSize={420} minSize={220}>
            {renderSlideRail('teaching-presentation-slide-rail-desktop')}
          </ResizablePanel>
          <ResizableHandle aria-label={ui("Resize slide rail")} />
          <ResizablePanel>
            {renderStage()}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      </main>
    </>
  )
}
